/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  FullProject,
  LoggedUser,
  PendingAction,
  PendingActionsCounts,
  isLeader,
  projectStatusConfig,
  taskStatusesConfig,
} from 'entities';
import { GetLabelFn } from 'types/general';

import { listGet } from 'utils/array';

export type LocalPendingAction =
  | 'TASK_DATE_CHANGE'
  | 'PROJECT_DATE_CHANGE'
  | 'ADD_COUNTRY_TO_PROJECT'
  | 'UNASSIGNED_TASKS'
  | 'PROJECT_START_AUTHORIZATION'
  | 'OUTSOURCE_AUTHORIZATION';

export type FullPendingActions = PendingAction | LocalPendingAction;

type AsyncConfig = { type: 'ASYNC' };

type ResolverParams = { user: LoggedUser; projects: FullProject[] };

type LocalConfig = {
  type: 'LOCAL';
  resolver: (params: ResolverParams) => any[];
  permissionCheck: (user: LoggedUser) => boolean;
};

type BasePendingActionConfig = {
  queryParam: string;
  getLabel: GetLabelFn;
};

type AsyncFullConfig = BasePendingActionConfig & AsyncConfig;
type LocalFullConfig = BasePendingActionConfig & LocalConfig;

type AsyncMapping = Record<PendingAction, AsyncFullConfig>;
type LocalMapping = Record<LocalPendingAction, LocalFullConfig>;

type PendingConfigBase = LocalMapping & AsyncMapping;

function buildPendingConfig<Config extends PendingConfigBase>(config: Config): Config {
  return config;
}

export const pendingActionConfig = buildPendingConfig({
  UNASSIGNED_PROJECTS: {
    type: 'ASYNC',
    getLabel: (t) => t('project-assign'),
    queryParam: 'projectAssignment',
  },

  UNASSIGNED_INVOICE_CONTROLS: {
    type: 'ASYNC',
    getLabel: (t) => t('invoice-assign'),
    queryParam: 'invoiceAssign',
  },

  PENDING_QUOTATION_REQUESTS: {
    type: 'ASYNC',
    getLabel: (t) => t('quotation-requests'),
    queryParam: 'quotationRequests',
  },

  ACTIVE_DISCOUNT_REQUESTS: {
    type: 'ASYNC',
    getLabel: (t) => t('discount-requests'),
    queryParam: 'discountRequests',
  },

  INVOICE_CANCELATION_REQUESTS: {
    type: 'ASYNC',
    getLabel: (t) => t('invoice-cancelation'),
    queryParam: 'invoiceCancelation',
  },

  INVOICE_CONTROL_CANCEL_REQUESTS: {
    type: 'ASYNC',
    getLabel: (t) => t('project-invoicing-cancelations'),
    queryParam: 'invoiceControlCancelation',
  },

  PROJECT_REPRICE_REQUESTS: {
    type: 'ASYNC',
    getLabel: (t) => t('repricing'),
    queryParam: 'repricing',
  },

  UNASSIGNED_RECRUITMENTS: {
    type: 'ASYNC',
    getLabel: (t) => t('recruitment-assignment'),
    queryParam: 'recruitmentAssignment',
  },

  PROJECT_TEAM_CHANGE_REQUESTS: {
    type: 'ASYNC',
    getLabel: (t) => t('project-transfers'),
    queryParam: 'projectTransfers',
  },

  TASK_DATE_CHANGE: {
    getLabel: (t) => t('task-date-changes'),

    queryParam: 'dateChangeRequests',

    type: 'LOCAL',

    permissionCheck: (user) => [isLeader(user), user.team.canAssumeProject].every(Boolean),

    resolver: ({ projects, user }) =>
      projects
        .map(({ name, tasks, owner }) =>
          tasks
            .filter(({ dateChangeRequest }) => owner.id === user.id && dateChangeRequest)
            .map((task) => ({ ...task, projectName: name })),
        )
        .flat(),
  },

  ADD_COUNTRY_TO_PROJECT: {
    getLabel: (t) => t('add-country'),

    queryParam: 'countryAddition',

    type: 'LOCAL',

    permissionCheck: (user) => [isLeader(user), user.team.canApproveCountryAddition].every(Boolean),

    resolver: ({ projects }) =>
      projects.filter(({ addCountryRequest }) => addCountryRequest?.status === 'REQUESTED'),
  },

  OUTSOURCE_AUTHORIZATION: {
    getLabel: (t) => t('outsourcing'),

    queryParam: 'outsourcing',

    type: 'LOCAL',

    permissionCheck: (user) =>
      [isLeader(user), user.team.outsourcing === 'AUTHORIZE'].every(Boolean),

    resolver: ({ projects }) =>
      projects
        .map(({ services, name, id: projectId, clientName, owner, tasks }) =>
          services
            .filter((s) => s.outsourceConfig && !s.outsourceConfig.approved)
            .map(({ outsourceConfig, service, id }) => ({
              ...outsourceConfig!,
              projectId,
              projectName: name,
              clientName,
              id,
              owner,
              serviceName: listGet(tasks, service, 'service')?.baseName || '',
              service,
              justification: outsourceConfig!.justification!,
            })),
        )
        .flat(),
  },

  PROJECT_DATE_CHANGE: {
    getLabel: (t) => t('project-date-changes'),

    queryParam: 'projectDates',

    type: 'LOCAL',

    permissionCheck: (user) => [isLeader(user), user.team.canAssumeProject].every(Boolean),

    resolver: ({ projects, user }) =>
      projects.filter(
        ({ owner, expectedDateRequest }) => owner.team === user.team.id && expectedDateRequest,
      ),
  },

  PROJECT_START_AUTHORIZATION: {
    getLabel: (t) => t('project-starts'),

    queryParam: 'projectStartAuthorization',

    type: 'LOCAL',

    permissionCheck: (user) => [user.team.canEnableProjectToStart].every(Boolean),

    resolver: ({ projects }) =>
      projects.filter(
        ({ status, authorizedToStart }) =>
          !authorizedToStart && status === projectStatusConfig.notStated.code,
      ),
  },

  UNASSIGNED_TASKS: {
    getLabel: (t) => t('task-assign'),

    queryParam: 'taskAssignment',

    type: 'LOCAL',

    permissionCheck: (user) => [isLeader(user), user.team.hasServices].every(Boolean),

    resolver: ({ projects, user }) =>
      projects
        .map(({ tasks, name, id }) =>
          tasks
            .filter(
              ({ status, team, owner }) =>
                status === taskStatusesConfig.unassigned.code &&
                team === user.team.id &&
                !owner.email,
            )
            .map((task) => ({ ...task, projectName: name, projectId: id })),
        )
        .flat()
        .sort(({ expectedDate: expectedA }, { expectedDate: expectedB }) => {
          const defaultDate = '2040-04-22T12:00:00.000';

          const [dateA, dateB] = [expectedA, expectedB].map(
            (date) => new Date(date || defaultDate),
          );

          return Number(dateA) - Number(dateB);
        }),
  },
});

type PendingConfig = typeof pendingActionConfig;

type LocalEntries = {
  [LocalAction in LocalPendingAction]: Omit<
    PendingConfig[LocalAction],
    'resolver' | 'permissionCheck'
  > & {
    entries: ReturnType<PendingConfig[LocalAction]['resolver']>;
    count: number;
    action: LocalAction;
  };
};

type AsyncEntries = {
  [AsyncAction in PendingAction]: AsyncFullConfig & {
    action: AsyncAction;
    count: number;
  };
};

export type PendingEntries = AsyncEntries & LocalEntries;

function resolveSingleLocal({
  config,
  projects,
  user,
  action,
}: ResolverParams & {
  config: PendingConfig[LocalPendingAction];
  action: LocalPendingAction;
}): LocalEntries[keyof LocalEntries] {
  const { permissionCheck, resolver, ...rest } = config;

  const hasPermission = permissionCheck(user);

  if (!hasPermission) return { ...rest, count: 0, entries: [], action };

  const entries = resolver({ user, projects });

  return {
    ...rest,
    entries,
    count: entries.length,
    action,
  } as LocalEntries[keyof LocalEntries];
}

export function buildPendingEntries({
  projects,
  user,
  asyncCounts,
}: ResolverParams & {
  asyncCounts: PendingActionsCounts;
}): PendingEntries {
  return Object.fromEntries(
    Object.entries(pendingActionConfig).map(([action, config]) => [
      action,
      config.type === 'ASYNC'
        ? { ...config, action, count: asyncCounts.byType[action as PendingAction] ?? 0 }
        : resolveSingleLocal({ config, projects, user, action: action as LocalPendingAction }),
    ]),
  ) as PendingEntries;
}
